/**
 * Shortlist store — manages artifact panel data and gallery.
 */

import { create } from "zustand";
import type { Shortlist, ShortlistData, ShortlistSummary } from "@/api/types";
import { apiFetch, ApiError } from "@/api/client";

interface ShortlistState {
  activeShortlist: Shortlist | null;
  /** Streaming shortlist data from WebSocket (before persistence) */
  liveData: ShortlistData | null;
  gallery: ShortlistSummary[];
  galleryTotal: number;

  loadShortlist: (id: string) => Promise<void>;
  loadGallery: () => Promise<void>;
  setLiveData: (data: ShortlistData) => void;
}

export const useShortlistStore = create<ShortlistState>((set) => ({
  activeShortlist: null,
  liveData: null,
  gallery: [],
  galleryTotal: 0,

  loadShortlist: async (id: string) => {
    try {
      const shortlist = await apiFetch<Shortlist>(`/shortlists/${id}`);
      set({ activeShortlist: shortlist, liveData: shortlist.data });
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        // Shortlist was deleted or the conversation reference is stale — clear
        // state silently rather than producing an unhandled rejection.
        set({ activeShortlist: null, liveData: null });
        return;
      }
      throw e;
    }
  },

  loadGallery: async () => {
    const res = await apiFetch<{ shortlists: ShortlistSummary[]; total: number }>("/shortlists");
    set({ gallery: res.shortlists, galleryTotal: res.total });
  },

  setLiveData: (data: ShortlistData) => set({ liveData: data }),
}));
